import React from "react";
import { Linkedin, Facebook, Mail, MapPin, Phone, MessageCircle } from "lucide-react";
import { FOOTER } from "@/constants/testIds";

const cols = [
  {
    title: "Services",
    links: [
      { id: "ftl", label: "Full Truckload" },
      { id: "ltl", label: "Less-than-Truckload" },
      { id: "cross-border", label: "Cross-Border SADC" },
      { id: "abnormal", label: "Abnormal & Heavy Haul" },
      { id: "container", label: "Containerised" },
      { id: "reefer", label: "Refrigerated" },
    ],
  },
  {
    title: "Who we serve",
    links: [
      { id: "shippers-link", label: "Shippers" },
      { id: "carriers-link", label: "Carriers" },
      { id: "mining-link", label: "Mining & Heavy" },
      { id: "fmcg-link", label: "FMCG & Retail" },
      { id: "agri-link", label: "Agriculture" },
      { id: "manufacturing-link", label: "Manufacturing" },
    ],
  },
  {
    title: "Company",
    links: [
      { id: "about", label: "About us" },
      { id: "compliance-link", label: "Compliance" },
      { id: "process-link", label: "Our process" },
      { id: "testimonials-link", label: "Testimonials" },
      { id: "careers", label: "Careers" },
      { id: "contact", label: "Contact" },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid={FOOTER.section}
      className="relative bg-[#0A0F1C] text-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 pb-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div id="about" className="lg:col-span-4">
            <a href="#top" className="inline-flex items-center gap-2.5">
              <div className="bg-white rounded-xl p-2.5 inline-block">
                <img
                  src="/freightxchange-logo.png"
                  alt="FreightXchange"
                  className="h-8 w-auto"
                />
              </div>
            </a>
            <p className="mt-5 text-white/65 leading-relaxed max-w-sm">
              An independent freight brokerage connecting shippers and carriers
              across South Africa and the wider SADC region. Built on
              relationships, scaled by a vetted network.
            </p>

            <div className="mt-8 space-y-3 text-sm">
              <a
                href="mailto:hello@freightxchange.co.za"
                className="flex items-center gap-3 text-white/75 hover:text-white transition-colors"
                data-testid={FOOTER.link("email")}
              >
                <span className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <Mail className="h-4 w-4 text-[#00AEEF]" />
                </span>
                hello@freightxchange.co.za
              </a>
              <a
                href="tel:+27110000000"
                className="flex items-center gap-3 text-white/75 hover:text-white transition-colors"
                data-testid={FOOTER.link("phone")}
              >
                <span className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <Phone className="h-4 w-4 text-[#00AEEF]" />
                </span>
                +27 11 000 0000
              </a>
              <a
                href="https://wa.me/27000000000"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 text-white/75 hover:text-white transition-colors"
                data-testid={FOOTER.link("whatsapp")}
              >
                <span className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <MessageCircle className="h-4 w-4 text-[#00AEEF]" />
                </span>
                WhatsApp us
              </a>
              <div className="flex items-center gap-3 text-white/75">
                <span className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-[#00AEEF]" />
                </span>
                Gauteng · Servicing Southern Africa
              </div>
            </div>

            <div className="mt-8 flex items-center gap-3">
              <a
                href="#"
                aria-label="LinkedIn"
                data-testid={FOOTER.link("linkedin")}
                className="h-10 w-10 rounded-lg border border-white/10 bg-white/5 hover:bg-[#0070C0] hover:border-[#0070C0] flex items-center justify-center transition-colors"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a
                href="#"
                aria-label="Facebook"
                data-testid={FOOTER.link("facebook")}
                className="h-10 w-10 rounded-lg border border-white/10 bg-white/5 hover:bg-[#0070C0] hover:border-[#0070C0] flex items-center justify-center transition-colors"
              >
                <Facebook className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-6">
            {cols.map((c) => (
              <div key={c.title}>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#00AEEF]">
                  {c.title}
                </div>
                <ul className="mt-5 space-y-3">
                  {c.links.map((l) => (
                    <li key={l.id}>
                      <a
                        href={`#${l.id}`}
                        data-testid={FOOTER.link(l.id)}
                        className="text-[15px] text-white/75 hover:text-white transition-colors"
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-sm text-white/55">
          <div>© {new Date().getFullYear()} FreightXchange (Pty) Ltd. All rights reserved.</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <a href="#" className="hover:text-white transition-colors" data-testid={FOOTER.link("privacy")}>
              Privacy (POPIA)
            </a>
            <a href="#" className="hover:text-white transition-colors" data-testid={FOOTER.link("terms")}>
              Terms
            </a>
            <a href="#" className="hover:text-white transition-colors" data-testid={FOOTER.link("paia")}>
              PAIA Manual
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
